import {
	Connection,
	Controls,
	EdgeChange,
	MiniMap,
	Node,
	NodeChange,
	Panel,
	ReactFlow,
	ReactFlowInstance,
	Viewport,
	XYPosition,
	addEdge,
	applyEdgeChanges,
	applyNodeChanges,
	useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { type CSSProperties, useCallback, useMemo, useRef, useState } from 'react';

import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BuildIcon from '@mui/icons-material/Build';
import CloseIcon from '@mui/icons-material/Close';
import {
	Box,
	Button,
	Card,
	CardActionArea,
	CardContent,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	Typography,
	alpha,
	useTheme,
} from '@mui/material';

import { useReactFlowAutoFit } from '@@common';
import { uuid } from '@@common';

import { usePortalTheme } from '../../../themes/PortalThemeProvider';
import { flowThemeTokens } from '../../../themes/flowTokens';
import useFlowMetadata from '../Hooks/Metadata/useFlowMetadata';
import { AGENT_TOOL_TRANSITION_TYPE, ActivityModel, FlowEdgeModel, TransitionModel } from '../Models/FlowModel';
import { FlowEdge, FlowNode } from '../Models/GraphModel';
import { createStableGraphNodes } from '../Services/GraphRenderState';
import { isContainerContentHidden } from '../Services/AgentServiceCompact';
import { isAllowedFlowConnectionScope } from '../Services/FlowConnectionScope';
import { hasOtherFlowConnection, reconnectFlowEdge } from '../Services/FlowEdgeReconnection';
import { DagreLayoutService } from '../Services/Layout/DagreLayoutService';
import { ActivityCategories, ActivityTypes } from './ActivityIcon';
import {
	getExecutionTraceStrokeWidth,
	type FlowExecutionState,
	type FlowExecutionTrace,
	FlowExecutionTraceProvider,
	mergeFlowExecutionState,
} from './Contexts/FlowExecutionTraceContext';
import FlowContextActions from './FlowContextActions';
import './FlowWorkspace.scss';
import ActivityNode, { ACTIVITY_NODE_TYPE } from './Nodes/ActivityNode';
import BlockNode, { BLOCK_NODE_TYPE } from './Nodes/BlockNode';
import GroupNode, { GROUP_NODE_TYPE } from './Nodes/GroupNode';
import NoteNode, { NOTE_NODE_TYPE } from './Nodes/NoteNode';
import OnErrorNode, { ON_ERROR_NODE_TYPE } from './Nodes/OnErrorNode';
import SectionNode, { SECTION_NODE_TYPE } from './Nodes/SectionNode';
import TransitionEdge, { TRANSITION_EDGE_TYPE, edgeFromTransition } from './Nodes/TransitionEdge';

/**
 * Recursively computes a node's absolute canvas position by walking up the parent chain.
 * `nodePosition` is the node's stored position (relative to its parent if parentId is set).
 */
function getAbsolutePosition(nodePosition: XYPosition, parentId: string | undefined, nodesMap: Map<string, FlowNode>, depth = 0): XYPosition {
	if (!parentId || depth > 20) return nodePosition;
	const parent = nodesMap.get(parentId);
	if (!parent) return nodePosition;
	const parentAbs = getAbsolutePosition(parent.position, parent.parentId, nodesMap, depth + 1);
	return { x: parentAbs.x + nodePosition.x, y: parentAbs.y + nodePosition.y };
}

/** Strips a node's parentId and converts its position to absolute canvas coordinates. */
function detachNodeFromParent(node: FlowNode, nodesMap: Map<string, FlowNode>): FlowNode {
	const absPos = getAbsolutePosition(node.position, node.parentId, nodesMap);
	const { parentId: _p, extent: _e, hidden: _h, ...rest } = node as FlowNode & { extent?: unknown };
	return { ...rest, position: absPos };
}

/** Returns the rendered area of a node (falls back to Infinity so unresized nodes sort last). */
function nodeArea(n: FlowNode): number {
	return (n.measured?.width ?? n.width ?? Infinity) * (n.measured?.height ?? n.height ?? Infinity);
}

/** Returns the smallest (innermost) node from a list, by rendered area. */
function pickSmallestNode(nodes: FlowNode[]): FlowNode {
	return nodes.reduce((smallest, n) => (nodeArea(n) < nodeArea(smallest) ? n : smallest));
}

/** Builds a parent-id → child-ids index from a node map. */
function buildChildrenMap(nodesMap: Map<string, FlowNode>): Map<string, string[]> {
	const childrenOf = new Map<string, string[]>();
	for (const n of Array.from(nodesMap.values())) {
		if (n.parentId) {
			const list = childrenOf.get(n.parentId);
			if (list) list.push(n.id);
			else childrenOf.set(n.parentId, [n.id]);
		}
	}
	return childrenOf;
}

/** Collects the ids of a node and all its descendants. */
function collectDescendantIds(rootId: string, nodesMap: Map<string, FlowNode>): Set<string> {
	const childrenOf = buildChildrenMap(nodesMap);
	const ids = new Set<string>();
	const queue = [rootId];
	while (queue.length > 0) {
		const id = queue.pop()!;
		ids.add(id);
		const children = childrenOf.get(id);
		if (children) queue.push(...children);
	}
	return ids;
}

/** Sets a node's parentId and converts its absolute position to parent-relative coordinates. */
function attachNodeToParent(node: FlowNode, newParent: FlowNode, nodesMap: Map<string, FlowNode>): FlowNode {
	const parentAbsPos = getAbsolutePosition(newParent.position, newParent.parentId, nodesMap);
	const parentHidesChildren = isContainerContentHidden(newParent.data);
	return {
		...node,
		parentId: newParent.id,
		position: { x: node.position.x - parentAbsPos.x, y: node.position.y - parentAbsPos.y },
		hidden: parentHidesChildren,
	};
}

/** Returns z-order priority (lower = rendered earlier = behind other nodes). */
function zOrderPriority(node: FlowNode): number {
	switch (node.type) {
		case SECTION_NODE_TYPE:
			return 0; // Sections render in the back (can't block clicks on activities)
		case BLOCK_NODE_TYPE:
		case GROUP_NODE_TYPE:
			return 1; // Blocks in the middle
		default:
			return 2; // Activities, notes, etc. render on top
	}
}

/**
 * React Flow requires parent nodes to appear before their children in the nodes array.
 * Also sorts siblings by z-order priority so Sections render behind activities.
 */
function sortNodesByParentFirst(nodes: FlowNode[]): FlowNode[] {
	const nodesMap = new Map(nodes.map((n) => [n.id, n]));
	const childrenOf = buildChildrenMap(nodesMap);
	const result: FlowNode[] = [];
	const visited = new Set<string>();
	const enqueue = (id: string) => {
		if (visited.has(id)) return;
		visited.add(id);
		const node = nodesMap.get(id);
		if (!node) return;
		result.push(node);
		// Process children in z-order (Sections first, then blocks, then activities)
		const children = childrenOf.get(id) ?? [];
		const sortedChildren = children
			.map((childId) => ({ id: childId, priority: zOrderPriority(nodesMap.get(childId)!) }))
			.sort((a, b) => a.priority - b.priority);
		for (const { id: childId } of sortedChildren) enqueue(childId);
	};
	// Start with roots (no parentId, or parent not present in this set), z-ordered
	const roots = nodes.filter((n) => !n.parentId || !nodesMap.has(n.parentId));
	const sortedRoots = roots.sort((a, b) => zOrderPriority(a) - zOrderPriority(b));
	for (const node of sortedRoots) enqueue(node.id);
	// Append anything left (shouldn't happen, but guards against cycles)
	for (const node of nodes) {
		if (!visited.has(node.id)) result.push(node);
	}
	return result;
}

const nodeTypes = {
	[ACTIVITY_NODE_TYPE]: ActivityNode,
	[BLOCK_NODE_TYPE]: BlockNode,
	[GROUP_NODE_TYPE]: GroupNode,
	[NOTE_NODE_TYPE]: NoteNode,
	[ON_ERROR_NODE_TYPE]: OnErrorNode,
	[SECTION_NODE_TYPE]: SectionNode,
};
const edgeTypes = {
	[TRANSITION_EDGE_TYPE]: TransitionEdge,
};

const layoutOptions = [
	{ label: 'Layout vertically', value: 'TB' },
	{ label: 'Layout horizontally', value: 'LR' },
];

export type FlowWorkspaceProps = {
	nodes: FlowNode[];
	setNodes: React.Dispatch<React.SetStateAction<FlowNode[]>>;
	edges: FlowEdge[];
	setEdges: React.Dispatch<React.SetStateAction<FlowEdge[]>>;
	executionTrace?: FlowExecutionTrace;
	setNodePosition?: (nodeId: string, p: XYPosition) => void;
	setViewport?: (viewport: Viewport) => void;
	setInstance?: (instance: ReactFlowInstance<FlowNode, FlowEdge>) => void;
	onActivityDrop?: (activityType: string, position: XYPosition) => void;
	readOnly?: boolean;
};

function resolveVisibleNodeId(nodeId: string | undefined, nodeById: Map<string, FlowNode>) {
	if (!nodeId) {
		return undefined;
	}

	let currentNode = nodeById.get(nodeId);
	while (currentNode?.hidden && currentNode.parentId) {
		currentNode = nodeById.get(currentNode.parentId);
	}

	return currentNode?.id ?? nodeId;
}

const FlowGraph: React.FC<FlowWorkspaceProps> = ({ nodes, setNodes, edges, setEdges, executionTrace, setViewport, setInstance, onActivityDrop, readOnly }) => {
	const theme = useTheme();
	const { themeName } = usePortalTheme();
	const tokens = flowThemeTokens[themeName];
	const { fitView, getNodes, getEdges, getIntersectingNodes, screenToFlowPosition } = useReactFlow<FlowNode, FlowEdge>();
	const [pendingAgentConnection, setPendingAgentConnection] = useState<Connection | null>(null);
	const nodeColor = useCallback(
		(node: Node) => {
			switch (node.type) {
				case ACTIVITY_NODE_TYPE:
					return theme.palette.primary.main;
				case BLOCK_NODE_TYPE:
				case GROUP_NODE_TYPE:
					return tokens.block;
				case NOTE_NODE_TYPE:
					return tokens.note;
				case SECTION_NODE_TYPE:
					return tokens.start;
				case ON_ERROR_NODE_TYPE:
					return tokens.error;
				default:
					return theme.palette.primary.main;
			}
		},
		[theme.palette.primary.main, tokens],
	);

	/**
	 * Tracks IDs of nodes that were detached from a parent in onNodeDragStart.
	 * Used in onNodeDragStop to pick the correct position source per node:
	 * - Detached nodes: position was explicitly set to absolute by onNodeDragStart → use currentNode.position
	 * - Top-level nodes: React Flow tracks absolute position internally → use dragged.position
	 */
	const dragStartedAsChildRef = useRef(new Set<string>());

	// Auto fit view when nodes are first loaded (fires after nodes are measured)
	useReactFlowAutoFit();

	const onLayout = useCallback(
		(direction: string) => {
			if (readOnly) {
				return;
			}

			const layoutService = new DagreLayoutService({ direction: direction });
			const layoutResult = layoutService.getLayoutedElements(getNodes(), getEdges());

			setNodes(layoutResult.nodes);
			setEdges(layoutResult.edges);

			window.requestAnimationFrame(() => {
				fitView({ padding: 0.2 });
			});
		},
		[getNodes, getEdges, setNodes, setEdges, fitView, readOnly],
	);

	const { activityByName } = useFlowMetadata();
	const reconnectingEdgeRef = useRef<FlowEdge | null>(null);

	const isValidConnection = useCallback(
		(connection: Connection | FlowEdge, existingTransitionType?: string | null) => {
			if (readOnly) return false;
			const reconnectingEdge = reconnectingEdgeRef.current;
			const nodes = getNodes();
			const edges = getEdges();
			if (!connection.source || !connection.target) return false;

			// Prevent self-connections
			if (connection.source === connection.target) return false;

			const sourceNode = nodes.find((n) => n.id === connection.source);
			const targetNode = nodes.find((n) => n.id === connection.target);

			if (!sourceNode || !targetNode) return false;

			const sourceActivity = sourceNode.data as ActivityModel;
			const targetActivity = targetNode.data as ActivityModel;

			const sourceMetadata = activityByName.get(sourceActivity.Type);
			const targetMetadata = activityByName.get(targetActivity.Type);

			const isAgent = sourceMetadata?.categories?.includes(ActivityCategories.AGENT) === true;
			const parentBlock = nodes.find((node) => node.id === sourceNode.parentId);
			const isParentScopeBlockTool = parentBlock?.data.Type === ActivityTypes.BLOCK_TOOL
				&& targetActivity.Type === ActivityTypes.BLOCK_TOOL
				&& targetNode.id !== parentBlock.id
				&& (targetNode.parentId ?? null) === (parentBlock.parentId ?? null);
			const transitionType = reconnectingEdge ? reconnectingEdge.data?.Type ?? null : existingTransitionType === undefined && 'data' in connection
				? (connection as FlowEdge).data?.Type ?? null
				: existingTransitionType;
			const isAgentTool = transitionType === undefined || transitionType?.toLowerCase() === AGENT_TOOL_TRANSITION_TYPE.toLowerCase();
			const isParentScopeProgressSink = parentBlock?.data.Type === ActivityTypes.BLOCK_TOOL
				&& targetActivity.Type === ActivityTypes.AGENT_PROGRESS_SINK
				&& (targetNode.parentId ?? null) === (parentBlock.parentId ?? null)
				&& (transitionType === undefined || transitionType?.toLowerCase() !== AGENT_TOOL_TRANSITION_TYPE.toLowerCase());
			// A nested agent can invoke a sibling BlockTool in its parent's scope, but cannot
			// leave its containing BlockTool through an ordinary flow transition. A direct
			// progress-sink edge is observational and does not advance flow execution.
			if (!isAllowedFlowConnectionScope(sourceNode, targetNode, nodes,
				isAgent && ((isParentScopeBlockTool && isAgentTool) || isParentScopeProgressSink))) return false;

			// Rule 1: AgentService activities can only connect to Agent activities (not non-Agent targets)
			if (sourceMetadata?.categories?.includes(ActivityCategories.AGENT_SERVICE)) {
				if (!targetMetadata?.categories?.includes(ActivityCategories.AGENT)) {
					return false;
				}
			}

			// Rule 2: Only Agent activities can connect to AgentService activities
			if (targetMetadata?.categories?.includes(ActivityCategories.AGENT_SERVICE)) {
				if (!sourceMetadata?.categories?.includes(ActivityCategories.AGENT)) {
					return false;
				}

				// Rule 3: Only allow one connection from an Agent to an AgentService
				if (hasOtherFlowConnection(edges, connection, reconnectingEdge?.id)) {
					return false;
				}
			}

			return true;
		},
		[getNodes, getEdges, activityByName, readOnly],
	);

	// Apply the same scope and activity rules when reconnecting an existing edge.
	const onEdgeUpdate = useCallback(
		(oldEdge: FlowEdge, newConnection: Connection) => {
			if (readOnly || !isValidConnection(newConnection, oldEdge.data?.Type ?? null)) {
				return;
			}

			setEdges((edges) => edges.map((edge) => edge.id === oldEdge.id ? reconnectFlowEdge(edge, newConnection) : edge));
		},
		[setEdges, readOnly, isValidConnection],
	);

	const addConnection = useCallback(
		(connection: Connection, asAgentTool: boolean) => {
			if (readOnly) {
				return;
			}

			// isValidConnection is already called by ReactFlow before onConnect fires
			const sourceNode = nodes.find((n) => n.id === connection.source);
			if (!sourceNode) return;

			const transitionModel: TransitionModel = {
				Id: uuid(),
				To: connection.target,
				...(asAgentTool ? { Type: AGENT_TOOL_TRANSITION_TYPE } : {}),
				Metadata: {
					sourceHandle: connection.sourceHandle,
					targetHandle: connection.targetHandle,
				},
			};
			const edge = edgeFromTransition(transitionModel, sourceNode.data as ActivityModel);
			setEdges((eds) => addEdge(edge, eds));
		},
		[nodes, setEdges, readOnly],
	);

	const onConnect = useCallback(
		(connection: Connection) => {
			if (readOnly) return;

			const sourceNode = nodes.find((node) => node.id === connection.source);
			const targetNode = nodes.find((node) => node.id === connection.target);
			const sourceMetadata = sourceNode ? activityByName.get(sourceNode.data.Type) : undefined;
			const targetMetadata = targetNode ? activityByName.get(targetNode.data.Type) : undefined;
			const parentBlock = nodes.find((node) => node.id === sourceNode?.parentId);
			if (sourceMetadata?.categories?.includes(ActivityCategories.AGENT)
				&& parentBlock?.data.Type === ActivityTypes.BLOCK_TOOL
				&& targetNode?.data.Type === ActivityTypes.BLOCK_TOOL
				&& targetNode.id !== parentBlock.id
				&& (targetNode.parentId ?? null) === (parentBlock.parentId ?? null)) {
				addConnection(connection, true);
				return;
			}
			const targetIsTool = targetMetadata?.categories?.includes(ActivityCategories.TOOL) === true;
			const canChooseAgentTool =
				sourceMetadata?.categories?.includes(ActivityCategories.AGENT) === true &&
				(targetMetadata?.categories?.includes(ActivityCategories.AGENT_SERVICE) !== true || targetIsTool);

			if (canChooseAgentTool) {
				setPendingAgentConnection(connection);
				return;
			}

			addConnection(connection, false);
		},
		[activityByName, addConnection, nodes, readOnly],
	);
	const onEdgesChange = useCallback(
		(changes: EdgeChange<FlowEdge>[]) => {
			const allowedChanges = readOnly ? changes.filter((change) => change.type === 'select') : changes;
			if (allowedChanges.length > 0) {
				setEdges((eds) => applyEdgeChanges(allowedChanges, eds));
			}
		},
		[setEdges, readOnly],
	);
	const onNodesChange = useCallback(
		(changes: NodeChange<FlowNode>[]) => {
			const allowedChanges = readOnly ? changes.filter((change) => change.type === 'select' || change.type === 'dimensions') : changes;
			if (allowedChanges.length > 0) {
				setNodes((nds) => applyNodeChanges<FlowNode>(allowedChanges, nds));
			}
		},
		[setNodes, readOnly],
	);

	const onViewportChange = useCallback((viewport: Viewport) => setViewport?.(viewport), [setViewport]);

	const onDragOver = useCallback(
		(event: React.DragEvent) => {
			if (readOnly || !event.dataTransfer.types.includes('application/x-randol-flow-activity')) {
				return;
			}

			event.preventDefault();
			event.dataTransfer.dropEffect = 'copy';
		},
		[readOnly],
	);

	const onDrop = useCallback(
		(event: React.DragEvent) => {
			if (readOnly) {
				return;
			}

			const activityType = event.dataTransfer.getData('application/x-randol-flow-activity');
			if (!activityType) {
				return;
			}

			event.preventDefault();
			onActivityDrop?.(activityType, screenToFlowPosition({ x: event.clientX, y: event.clientY }));
		},
		[onActivityDrop, readOnly, screenToFlowPosition],
	);

	const selectStructuralNodes = useMemo(() => createStableGraphNodes<FlowNode>(), []);
	const structuralNodes = selectStructuralNodes(nodes);
	const visibleNodeStates = useMemo(() => {
		const nodeById = new Map(structuralNodes.map((node) => [node.id, node]));
		const visibleNodeStates = new Map<string, FlowExecutionState>();
		executionTrace?.nodeStates.forEach((state, nodeId) => {
			const visibleNodeId = resolveVisibleNodeId(nodeId, nodeById);
			if (visibleNodeId) {
				visibleNodeStates.set(visibleNodeId, mergeFlowExecutionState(visibleNodeStates.get(visibleNodeId), state));
			}
		});

		return visibleNodeStates;
	}, [structuralNodes, executionTrace]);
	const visibleNodeVisitCounts = useMemo(() => {
		const nodeById = new Map(structuralNodes.map((node) => [node.id, node]));
		const visibleNodeCounts = new Map<string, number>();
		executionTrace?.nodeVisitCounts.forEach((count, nodeId) => {
			const visibleNodeId = resolveVisibleNodeId(nodeId, nodeById);
			if (visibleNodeId) {
				visibleNodeCounts.set(visibleNodeId, (visibleNodeCounts.get(visibleNodeId) ?? 0) + count);
			}
		});
		return visibleNodeCounts;
	}, [structuralNodes, executionTrace]);
	const decoratedNodes = useMemo(() => new WeakMap<FlowNode, FlowNode>(), [visibleNodeStates, visibleNodeVisitCounts]);
	const renderedNodes = useMemo(() => {
		return nodes.map((node) => {
			const cached = decoratedNodes.get(node);
			if (cached) { return cached; }
			const executionState = visibleNodeStates.get(node.id);
			const traceClass = executionState === 'active' ? 'flow-node--trace-active' : executionState === 'visited' ? 'flow-node--trace-visited' : '';
			const baseClassName = (node.className ?? '').replace(/\bflow-node--trace-(active|visited)\b/g, '').trim();
			const className = [baseClassName, traceClass].filter(Boolean).join(' ');
			const traceWidth = executionState ? `${getExecutionTraceStrokeWidth(visibleNodeVisitCounts.get(node.id) ?? 1)}px` : undefined;
			const style = traceWidth ? { ...node.style, '--flow-trace-width': traceWidth } as CSSProperties : node.style;
			const hasSameClassName = className === (node.className ?? '');
			const existingTraceWidth = (node.style as (CSSProperties & { '--flow-trace-width'?: string }) | undefined)?.['--flow-trace-width'];
			const hasSameTraceWidth = !traceWidth || existingTraceWidth === traceWidth;

			const rendered = hasSameClassName && hasSameTraceWidth
				? node
				: {
						...node,
						className: className || undefined,
						style,
					};
			decoratedNodes.set(node, rendered);
			return rendered;
		});
	}, [nodes, visibleNodeStates, visibleNodeVisitCounts, decoratedNodes]);

	/**
	 * On drag start, detach child nodes from their parent so React Flow tracks them as
	 * top-level nodes during the drag. This eliminates the per-frame parent-relative
	 * coordinate transform that makes dragging sluggish.
	 */
	const onNodeDragStart = useCallback(
		(_event: React.MouseEvent, _node: FlowNode, draggedNodes: FlowNode[]) => {
			if (readOnly) {
				return;
			}

			dragStartedAsChildRef.current = new Set(draggedNodes.filter((n) => !!n.parentId).map((n) => n.id));
			setNodes((currentNodes) => {
				const draggedIds = new Set(draggedNodes.map((d) => d.id));
				const nodesMap = new Map(currentNodes.map((n) => [n.id, n]));
				return currentNodes.map((n) => {
					if (!draggedIds.has(n.id) || !n.parentId) return n;
					return detachNodeFromParent(n, nodesMap);
				});
			});
		},
		[setNodes, readOnly],
	);

	/**
	 * On drag stop, reparent the node if it was dropped inside a block.
	 * Because onNodeDragStart already removed parentId, dragged.position is absolute here.
	 * Nodes dropped outside any block simply remain top-level.
	 */
	const onNodeDragStop = useCallback(
		(_event: React.MouseEvent, _node: FlowNode, draggedNodes: FlowNode[]) => {
			if (readOnly) {
				return;
			}

			const detachedIds = dragStartedAsChildRef.current;
			setNodes((currentNodes) => {
				const nodesMap = new Map(currentNodes.map((n) => [n.id, n]));
				let updated = currentNodes;

				for (const dragged of draggedNodes) {
					const currentNode = nodesMap.get(dragged.id);
					if (!currentNode) continue;

					// Exclude the node itself and all its descendants from being a candidate parent
					// (prevents cycles when dragging a block that contains other blocks).
					const selfAndDescendants = collectDescendantIds(dragged.id, nodesMap);

					// Find block nodes the dragged node is overlapping
					const intersectingBlocks = getIntersectingNodes(dragged).filter(
						(n) => (n.type === BLOCK_NODE_TYPE || n.type === GROUP_NODE_TYPE) && !selfAndDescendants.has(n.id),
					);

					if (intersectingBlocks.length === 0) continue; // Stays top-level — nothing to do

					// Pick the smallest (innermost) block as the new parent
					const newParent = pickSmallestNode(intersectingBlocks);

					// Guard: already parented to this block — nothing to do (handles double-fire from React Flow)
					if (currentNode.parentId === newParent.id) continue;

					// Position source depends on where the drag originated:
					// - Previously a child: onNodeDragStart explicitly set an absolute position in state
					//   → currentNode.position is reliable (React Flow's dragged arg still holds the stale relative value)
					// - Always top-level: React Flow tracks absolute position internally throughout the drag
					//   → dragged.position is the correct final drop position
					//   (currentNode.position may lag because onNodesChange fires after onNodeDragStop)
					const absolutePosition = detachedIds.has(dragged.id) ? currentNode.position : dragged.position;
					const updatedNode = attachNodeToParent({ ...currentNode, position: absolutePosition }, newParent, nodesMap);

					updated = updated.map((n) => (n.id === dragged.id ? updatedNode : n));
					nodesMap.set(dragged.id, updatedNode);
				}

				return sortNodesByParentFirst(updated);
			});
		},
		[getIntersectingNodes, setNodes, readOnly],
	);

	return (
		<FlowExecutionTraceProvider trace={executionTrace}>
			<ReactFlow
				nodes={renderedNodes}
				edges={edges}
				onNodesChange={onNodesChange}
				onEdgesChange={onEdgesChange}
				onReconnect={onEdgeUpdate}
				onReconnectStart={(_, edge) => { reconnectingEdgeRef.current = edge; }}
				onReconnectEnd={() => { reconnectingEdgeRef.current = null; }}
				edgesReconnectable={!readOnly}
				reconnectRadius={16}
				onConnect={onConnect}
				isValidConnection={isValidConnection}
				nodeTypes={nodeTypes}
				edgeTypes={edgeTypes}
				onViewportChange={onViewportChange}
				onNodeDragStart={onNodeDragStart}
				onNodeDragStop={onNodeDragStop}
				onDragOver={onDragOver}
				onDrop={onDrop}
				onInit={setInstance}
				nodesDraggable={!readOnly}
				nodesConnectable={!readOnly}
				elementsSelectable
			>
				<MiniMap nodeColor={nodeColor} nodeStrokeWidth={3} zoomable pannable />
				<Controls />
				<Panel position="top-right">{!readOnly && <FlowContextActions options={layoutOptions} onSelected={onLayout} />}</Panel>
			</ReactFlow>
			<Dialog open={pendingAgentConnection !== null} onClose={() => setPendingAgentConnection(null)}>
				<DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
					Connect activity
					<IconButton aria-label="Close connection type dialog" onClick={() => setPendingAgentConnection(null)} edge="end" size="small">
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					<Typography color="text.secondary" sx={{ mb: 2 }}>
						Choose how the connected activity should be used with the Agent.
					</Typography>
					<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
						<Card
							variant="outlined"
							sx={{
								flex: 1,
								borderColor: 'divider',
								transition: 'border-color 0.2s, box-shadow 0.2s',
								'&:hover': { borderColor: 'primary.main', boxShadow: 3 },
							}}
						>
							<CardActionArea
								aria-label="Connect as normal activity"
								onClick={() => {
									if (pendingAgentConnection) addConnection(pendingAgentConnection, false);
									setPendingAgentConnection(null);
								}}
								sx={{ height: '100%' }}
							>
								<CardContent
									sx={{ minHeight: 150, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 1 }}
								>
									<AccountTreeIcon color="primary" sx={{ fontSize: 42 }} />
									<Typography variant="h6">Normal activity</Typography>
									<Typography variant="body2" color="text.secondary">
										Run this activity as the next step in the flow.
									</Typography>
								</CardContent>
							</CardActionArea>
						</Card>
						<Card
							variant="outlined"
							sx={{
								flex: 1,
								borderColor: 'info.main',
								transition: 'border-color 0.2s, box-shadow 0.2s',
								'&:hover': { borderColor: 'info.dark', boxShadow: 3 },
							}}
						>
							<CardActionArea
								aria-label="Connect as agent tool"
								onClick={() => {
									if (pendingAgentConnection) addConnection(pendingAgentConnection, true);
									setPendingAgentConnection(null);
								}}
								sx={{ height: '100%' }}
							>
								<CardContent
									sx={{ minHeight: 150, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 1 }}
								>
									<BuildIcon color="info" sx={{ fontSize: 42 }} />
									<Typography variant="h6">Agent tool</Typography>
									<Typography variant="body2" color="text.secondary">
										Expose this activity for the agent to call when needed.
									</Typography>
								</CardContent>
							</CardActionArea>
						</Card>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setPendingAgentConnection(null)}>Cancel</Button>
				</DialogActions>
			</Dialog>
		</FlowExecutionTraceProvider>
	);
};

export default function FlowWorkspace(props: FlowWorkspaceProps) {
	const theme = useTheme();
	const { themeName } = usePortalTheme();
	const tokens = flowThemeTokens[themeName];
	const flowThemeVariables = {
		'--flow-workspace-background': theme.palette.background.canvas ?? tokens.canvas,
		'--flow-surface': theme.palette.background.paper,
		'--flow-node-surface': tokens.nodeSurface,
		'--flow-hover': theme.palette.action.hover,
		'--flow-border': theme.palette.divider,
		'--flow-border-hover': theme.palette.primary.light,
		'--flow-primary': theme.palette.primary.main,
		'--flow-primary-glow': alpha(theme.palette.primary.main, 0.3),
		'--flow-text-primary': theme.palette.text.primary,
		'--flow-text-secondary': theme.palette.text.secondary,
		'--flow-edge': theme.palette.text.secondary,
		'--flow-handle': theme.palette.divider,
		'--flow-grid': tokens.grid,
		'--flow-success': theme.palette.success.main,
		'--flow-success-light': theme.palette.success.light,
		'--flow-success-soft': alpha(theme.palette.success.main, 0.16),
		'--flow-success-glow': alpha(theme.palette.success.main, 0.42),
		'--flow-error': tokens.error,
		'--flow-error-soft': alpha(tokens.error, 0.16),
		'--flow-agent': tokens.agent,
		'--flow-agent-soft': alpha(tokens.agent, 0.15),
		'--flow-agent-glow': alpha(tokens.agent, 0.4),
		'--flow-trigger': tokens.trigger,
		'--flow-trigger-soft': alpha(tokens.trigger, 0.15),
		'--flow-trigger-glow': alpha(tokens.trigger, 0.5),
		'--flow-exit': tokens.exit,
		'--flow-exit-soft': alpha(tokens.exit, 0.15),
		'--flow-exit-glow': alpha(tokens.exit, 0.5),
		'--flow-start': tokens.start,
		'--flow-start-soft': alpha(tokens.start, 0.16),
		'--flow-start-glow': alpha(tokens.start, 0.55),
		'--flow-block': tokens.block,
		'--flow-block-soft': alpha(tokens.block, 0.14),
		'--flow-block-glow': alpha(tokens.block, 0.22),
		'--flow-note': tokens.note,
		'--flow-note-soft': alpha(tokens.note, 0.18),
		'--flow-note-strong': alpha(tokens.note, 0.3),
		'--flow-note-text': alpha(tokens.note, 0.95),
	} as CSSProperties;

	return (
		<Box sx={{ height: '100%' }} style={flowThemeVariables} className="flow-workspace">
			<FlowGraph {...props} />
		</Box>
	);
}
